import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { intlShape } from '../../util/reactIntl';
import { userLocation, locationBounds } from '../../util/maps';
import { findOptionsForSelectFilter } from '../../util/search';
import { useConfiguration } from '../../context/configurationContext';
import { DISCIPLINES_TO_SLIDE } from '../../components/DisciplineSlider/DisciplineSlider.helper';
import { buildLocalizedOutdoorRegions } from '../../config/outdoorRegions';
import { IconQuestionAnswer, LocationAutocompleteInput } from '../../components';

import css from './LandingPage.module.css';

const PLANNER_STEP_COUNT = 6;
const PEOPLE_MIN = 1;
const PEOPLE_MAX = 12;

const LEVEL_OPTIONS = [
  {
    id: 'veryEasy',
    labelId: 'LandingPage.level.veryEasy.label',
    textId: 'LandingPage.level.veryEasy.text',
    promptId: 'LandingPage.level.veryEasy.prompt',
    fill: 0,
  },
  {
    id: 'easy',
    labelId: 'LandingPage.level.easy.label',
    textId: 'LandingPage.level.easy.text',
    promptId: 'LandingPage.level.easy.prompt',
    fill: 1,
  },
  {
    id: 'intermediate',
    labelId: 'LandingPage.level.intermediate.label',
    textId: 'LandingPage.level.intermediate.text',
    promptId: 'LandingPage.level.intermediate.prompt',
    fill: 2,
  },
  {
    id: 'difficult',
    labelId: 'LandingPage.level.difficult.label',
    textId: 'LandingPage.level.difficult.text',
    promptId: 'LandingPage.level.difficult.prompt',
    fill: 3,
  },
];

const DATE_OPTIONS = [
  {
    id: 'flexible',
    labelId: 'LandingPage.date.flexible.label',
    textId: 'LandingPage.date.flexible.text',
    promptId: 'LandingPage.date.flexible.prompt',
  },
  {
    id: 'weekend',
    labelId: 'LandingPage.date.weekend.label',
    textId: 'LandingPage.date.weekend.text',
    promptId: 'LandingPage.date.weekend.prompt',
  },
  {
    id: 'day',
    labelId: 'LandingPage.date.day.label',
    textId: 'LandingPage.date.day.text',
    promptId: 'LandingPage.date.day.prompt',
  },
  {
    id: 'week',
    labelId: 'LandingPage.date.week.label',
    textId: 'LandingPage.date.week.text',
    promptId: 'LandingPage.date.week.prompt',
  },
  {
    id: 'specific',
    labelId: 'LandingPage.date.specific.label',
    textId: 'LandingPage.date.specific.text',
    promptId: 'LandingPage.date.specific.prompt',
  },
];

const GROUP_OPTIONS = [
  {
    id: 'group',
    labelId: 'LandingPage.group.group.label',
    textId: 'LandingPage.group.group.text',
    groupType: 'group',
    promptId: 'LandingPage.group.group.prompt',
  },
  {
    id: 'private',
    labelId: 'LandingPage.group.private.label',
    textId: 'LandingPage.group.private.text',
    groupType: 'private',
    promptId: 'LandingPage.group.private.prompt',
  },
];

const BUDGET_OPTIONS = [
  {
    id: 'flexible',
    labelId: 'LandingPage.budget.flexible.label',
    textId: 'LandingPage.budget.flexible.text',
    min: null,
    max: null,
  },
  {
    id: 'low',
    labelId: 'LandingPage.budget.low.label',
    textId: 'LandingPage.budget.low.text',
    min: 0,
    max: 100,
  },
  {
    id: 'mid',
    labelId: 'LandingPage.budget.mid.label',
    textId: 'LandingPage.budget.mid.text',
    min: 100,
    max: 250,
  },
  {
    id: 'high',
    labelId: 'LandingPage.budget.high.label',
    textId: 'LandingPage.budget.high.text',
    min: 250,
    max: 500,
  },
  {
    id: 'custom',
    labelId: 'LandingPage.budget.custom.label',
    textId: 'LandingPage.budget.custom.text',
    min: null,
    max: null,
  },
];

const BUDGET_UNITS = [
  {
    id: 'person',
    labelId: 'LandingPage.budgetUnit.person.label',
    promptId: 'LandingPage.budgetUnit.person.prompt',
  },
  {
    id: 'session',
    labelId: 'LandingPage.budgetUnit.session.label',
    promptId: 'LandingPage.budgetUnit.session.prompt',
  },
];

const LOCATION_SHORTCUTS = [
  {
    id: 'flexible',
    labelId: 'LandingPage.location.flexible.label',
    promptId: 'LandingPage.location.flexible.prompt',
  },
  {
    id: 'nearby-alps',
    labelId: 'LandingPage.location.nearbyAlps.label',
    promptId: 'LandingPage.location.nearbyAlps.prompt',
  },
  {
    id: 'switzerland',
    labelId: 'LandingPage.location.switzerland.label',
    promptId: 'LandingPage.location.switzerland.prompt',
  },
  {
    id: 'dolomites',
    labelId: 'LandingPage.location.dolomites.label',
    promptId: 'LandingPage.location.dolomites.prompt',
  },
];

const PLANNER_DISCIPLINES = DISCIPLINES_TO_SLIDE;

const toReadableText = value =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const getLocalizedLabel = (intl, key) =>
  key ? intl.formatMessage({ id: `General.${key}`, defaultMessage: toReadableText(key) }) : '';

const t = (intl, id, defaultMessage, values) =>
  intl.formatMessage({ id, defaultMessage }, values);

const getOptionLabel = (intl, option, values) =>
  t(intl, option.labelId, option.defaultLabel || '', values);

const getOptionText = (intl, option) => t(intl, option.textId, option.defaultText || '');

const getOptionPrompt = (intl, option) => t(intl, option.promptId, option.defaultPrompt || '');

const getPlannerLocationLabel = locationValue => {
  const value = typeof locationValue === 'string' ? { search: locationValue } : locationValue || {};
  return (
    value?.selectedPlace?.address ||
    value?.selectedPlace?.place_name ||
    value?.search ||
    ''
  ).trim();
};

const trimText = (value, maxLength) =>
  String(value || '')
    .trim()
    .slice(0, maxLength);

const formatDateLabel = value => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
};

const getDatePreferencePrompt = (intl, planner) => {
  const dateOption = DATE_OPTIONS.find(option => option.id === planner.dateMode) || DATE_OPTIONS[0];
  if (planner.dateMode !== 'specific') {
    return getOptionPrompt(intl, dateOption);
  }

  const start = formatDateLabel(planner.startDate);
  const end = formatDateLabel(planner.endDate);
  return start && end
    ? t(intl, 'LandingPage.date.specific.promptWithDates', 'specific dates from {start} to {end}', {
        start,
        end,
      })
    : t(intl, 'LandingPage.date.specific.promptIncomplete', 'specific dates, not selected yet');
};

const getDatePreferenceLabel = (intl, planner) => {
  const dateOption = DATE_OPTIONS.find(option => option.id === planner.dateMode) || DATE_OPTIONS[0];
  if (planner.dateMode !== 'specific') {
    return getOptionLabel(intl, dateOption);
  }

  const start = formatDateLabel(planner.startDate);
  const end = formatDateLabel(planner.endDate);
  return start && end ? `${start} - ${end}` : getOptionLabel(intl, dateOption);
};

const formatBudgetOptionLabel = (intl, option, currency) =>
  getOptionLabel(intl, option, { currency: currency || 'EUR' });

const getBudgetOption = planner =>
  BUDGET_OPTIONS.find(option => option.id === planner.budgetMode) || BUDGET_OPTIONS[0];

const getBudgetUnit = planner =>
  BUDGET_UNITS.find(option => option.id === planner.budgetUnit) || BUDGET_UNITS[0];

const parseBudgetAmount = value => {
  const number = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const getBudgetRange = planner => {
  const option = getBudgetOption(planner);
  if (option.id !== 'custom') {
    return { min: option.min, max: option.max };
  }

  const min = parseBudgetAmount(planner.budgetMin);
  const max = parseBudgetAmount(planner.budgetMax);
  return min != null && max != null && max < min ? { min: max, max: min } : { min, max };
};

const getBudgetRangeLabel = (intl, planner, currency) => {
  const option = getBudgetOption(planner);
  const { min, max } = getBudgetRange(planner);

  if (option.id === 'flexible') {
    return t(intl, 'LandingPage.budget.flexible.rangeLabel', 'Flexible budget');
  }

  if (min != null && max != null) {
    return min === 0
      ? t(intl, 'LandingPage.budget.upToRange', 'Up to {currency} {max}', { currency, max })
      : t(intl, 'LandingPage.budget.betweenRange', '{currency} {min} - {max}', {
          currency,
          min,
          max,
        });
  }

  if (min != null) {
    return t(intl, 'LandingPage.budget.fromRange', 'From {currency} {min}', { currency, min });
  }

  if (max != null) {
    return t(intl, 'LandingPage.budget.upToRange', 'Up to {currency} {max}', { currency, max });
  }

  return t(intl, 'LandingPage.budget.custom.rangeLabel', 'Custom budget');
};

const getBudgetPreferenceLabel = (intl, planner, currency) => {
  const rangeLabel = getBudgetRangeLabel(intl, planner, currency);
  if (planner.budgetMode === 'flexible') {
    return rangeLabel;
  }

  return `${rangeLabel} ${getOptionPrompt(intl, getBudgetUnit(planner))}`;
};

const getBudgetPreferencePrompt = (intl, planner, currency) => {
  const option = getBudgetOption(planner);
  if (option.id === 'flexible') {
    return t(
      intl,
      'LandingPage.budget.flexible.prompt',
      'flexible budget, prioritize fit, quality, availability, and transparent value'
    );
  }

  return `${getBudgetRangeLabel(intl, planner, currency)} ${getOptionPrompt(intl, getBudgetUnit(planner))}`;
};

const getBudgetSearchPrice = planner => {
  const { min, max } = getBudgetRange(planner);
  return min != null || max != null ? `${min || 0},${max || 2000}` : null;
};

const toPlainLatLng = value => {
  if (!value) {
    return null;
  }

  const lat = Number(value.lat);
  const lng = Number(value.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const toPlainBounds = value => {
  const ne = toPlainLatLng(value?.ne);
  const sw = toPlainLatLng(value?.sw);
  return ne && sw ? { ne, sw } : null;
};

const getLocalizedPlannerRegionNames = region => {
  const names = region?.names || {};
  return {
    english: names.en || null,
    french: names.fr || null,
    german: names.de || null,
    italian: names.it || null,
  };
};

const getPlannerLocationPreferences = ({ intl, planner, selectedCountry, selectedCountryRegions }) => {
  const selectedPlace = planner.location?.selectedPlace || null;
  const selectedRegion = selectedCountryRegions.find(region => region.id === planner.locationRegion) || null;
  const countryName = selectedCountry?.name || '';
  const regionName = selectedRegion?.name || '';
  const placeLabel = getPlannerLocationLabel(planner.location);
  const multiLangAddressParts = selectedPlace?.multiLangAddressParts || {};

  if (planner.locationMode === 'specific') {
    return {
      mode: 'specific',
      rawText: placeLabel || planner.location?.search || '',
      label: placeLabel || planner.location?.search || '',
      city: selectedPlace?.city || multiLangAddressParts?.english?.city || null,
      region: multiLangAddressParts?.english?.region || null,
      country: multiLangAddressParts?.english?.country || null,
      localizedRegionNames: {
        english: multiLangAddressParts?.english?.region || null,
        french: multiLangAddressParts?.french?.region || null,
        german: multiLangAddressParts?.german?.region || null,
        italian: multiLangAddressParts?.italian?.region || null,
      },
      localizedCountryNames: {
        english: multiLangAddressParts?.english?.country || null,
        french: multiLangAddressParts?.french?.country || null,
        german: multiLangAddressParts?.german?.country || null,
        italian: multiLangAddressParts?.italian?.country || null,
      },
      origin: toPlainLatLng(selectedPlace?.origin),
      bounds: toPlainBounds(selectedPlace?.bounds),
    };
  }

  if (planner.locationMode === 'current') {
    const currentRawText = t(
      intl,
      'LandingPage.location.currentRawText',
      'My current location'
    );
    const currentLabel = t(intl, 'LandingPage.location.nearMe.label', 'Near me');

    return {
      mode: 'nearMe',
      rawText: currentRawText,
      label: currentLabel,
      origin: toPlainLatLng(selectedPlace?.origin),
      bounds: toPlainBounds(selectedPlace?.bounds),
      radiusKm: 30,
    };
  }

  return {
    mode: 'flexible',
    rawText: regionName ? `${regionName}, ${countryName}` : countryName,
    label: regionName ? `${regionName}, ${countryName}` : countryName,
    country: selectedCountry?.names?.en || countryName || null,
    countryId: selectedCountry?.id || null,
    region: selectedRegion?.names?.en || regionName || null,
    regionId: selectedRegion?.id || null,
    localizedRegionNames: getLocalizedPlannerRegionNames(selectedRegion),
    localizedCountryNames: getLocalizedPlannerRegionNames(selectedCountry),
  };
};

const getPlannerPreferences = ({
  intl,
  planner,
  selectedCountry,
  selectedCountryRegions,
  selectedDisciplineLabel,
  selectedAbility,
  selectedGroup,
  currency,
  source,
}) => {
  const { min, max } = getBudgetRange(planner);
  return {
    source,
    discipline: planner.discipline,
    disciplineLabel: selectedDisciplineLabel,
    ability: planner.ability,
    abilityLabel: selectedAbility ? getOptionLabel(intl, selectedAbility) : null,
    location: getPlannerLocationPreferences({
      intl,
      planner,
      selectedCountry,
      selectedCountryRegions,
    }),
    timing: {
      mode: planner.dateMode,
      label: getDatePreferenceLabel(intl, planner),
      dateFrom: planner.dateMode === 'specific' ? planner.startDate || null : null,
      dateTo: planner.dateMode === 'specific' ? planner.endDate || null : null,
    },
    budget: {
      mode: planner.budgetMode,
      unit: planner.budgetUnit,
      label: getBudgetPreferenceLabel(intl, planner, currency),
      currency,
      min,
      max,
      isSoftPreference: true,
    },
    group: {
      groupType: selectedGroup?.groupType || null,
      label: selectedGroup ? getOptionLabel(intl, selectedGroup) : null,
      people: planner.people,
    },
  };
};

const readSavedPlannerState = persistKey => {
  if (!persistKey || typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(persistKey);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return saved?.planner ? saved : null;
  } catch {
    return null;
  }
};

const AIPlannerPreferences = props => {
  const { hasPromptRequest, intl, locale, onPlannerChange, onPromptRequest, persistKey, source } = props;
  const config = useConfiguration();
  const currency = config.currency || 'EUR';

  const [planner, setPlanner] = useState(() => {
    const saved = readSavedPlannerState(persistKey);
    return saved?.planner || {
      discipline: PLANNER_DISCIPLINES[0]?.key || 'mountainHiking',
      ability: LEVEL_OPTIONS[1].id,
      location: { search: 'Switzerland', predictions: [], selectedPlace: { address: 'Switzerland' } },
      locationMode: 'flexible',
      locationCountry: 'switzerland',
      locationRegion: null,
      dateMode: 'flexible',
      startDate: '',
      endDate: '',
      budgetMode: 'flexible',
      budgetUnit: 'person',
      budgetMin: '',
      budgetMax: '',
      group: null,
      people: 2,
    };
  });
  const [plannerStep, setPlannerStep] = useState(() => {
    const saved = readSavedPlannerState(persistKey);
    return saved?.plannerStep ?? 0;
  });
  const [locationStatus, setLocationStatus] = useState({ status: 'idle', message: '' });

  // Persist planner form state so the wizard survives same-session navigation and
  // auth redirects (sign up / log in and back).
  useEffect(() => {
    if (!persistKey || typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.setItem(persistKey, JSON.stringify({ planner, plannerStep }));
    } catch {
      // Ignore storage failures.
    }
  }, [planner, plannerStep, persistKey]);

  // Block implicit form submissions fired by native date-picker popups.
  // The browser fires the submit event synchronously in the same JS tick as onChange;
  // a setTimeout(0) clears the flag only after that synchronous chain, so runPlanner
  // can detect and ignore the implicit submit while still allowing explicit button clicks.
  const dateJustChangedRef = useRef(false);
  const dateJustChangedTimerRef = useRef(null);

  const onDateInputChange = useCallback((field, value) => {
    setPlanner(prev => ({ ...prev, [field]: value }));
    dateJustChangedRef.current = true;
    if (dateJustChangedTimerRef.current) clearTimeout(dateJustChangedTimerRef.current);
    dateJustChangedTimerRef.current = setTimeout(() => {
      dateJustChangedRef.current = false;
      dateJustChangedTimerRef.current = null;
    }, 0);
  }, []);

  const plannerDisciplines = useMemo(() => {
    const disciplineOptions = findOptionsForSelectFilter('discipline', config.search.defaultFilters)
      .filter(option => option.state !== 'archived')
      .map(option => option.key);
    const availableKeys = new Set(disciplineOptions);
    const visibleDisciplines = PLANNER_DISCIPLINES.filter(discipline =>
      availableKeys.has(discipline.key)
    );

    return visibleDisciplines.length > 0 ? visibleDisciplines : PLANNER_DISCIPLINES;
  }, [config.search.defaultFilters]);

  const plannerCountries = useMemo(() => buildLocalizedOutdoorRegions(locale), [locale]);
  const selectedCountry =
    plannerCountries.find(country => country.id === planner.locationCountry) || plannerCountries[0];
  const selectedCountryRegions = useMemo(() => selectedCountry?.regions || [], [selectedCountry]);
  const activePlannerStep = Math.min(plannerStep, PLANNER_STEP_COUNT - 1);
  const selectedLocationShortcut = LOCATION_SHORTCUTS.find(
    option => option.id === planner.locationMode
  );
  const selectedLocationLabel =
    planner.locationMode === 'specific'
      ? getPlannerLocationLabel(planner.location) ||
        t(intl, 'LandingPage.location.choosePlace', 'Choose a place')
      : planner.locationMode === 'current'
      ? t(intl, 'LandingPage.location.nearMe.label', 'Near me')
      : planner.locationMode === 'flexible' && getPlannerLocationLabel(planner.location)
      ? getPlannerLocationLabel(planner.location)
      : selectedLocationShortcut
      ? getOptionLabel(intl, selectedLocationShortcut)
      : t(intl, 'LandingPage.location.flexible.label', 'I am flexible');
  const selectedDisciplineLabel = getLocalizedLabel(intl, planner.discipline);
  const selectedAbility = LEVEL_OPTIONS.find(option => option.id === planner.ability) || LEVEL_OPTIONS[1];
  const selectedGroup = GROUP_OPTIONS.find(option => option.id === planner.group);
  const selectedDateLabel = getDatePreferenceLabel(intl, planner);
  const selectedBudgetLabel = getBudgetPreferenceLabel(intl, planner, currency);
  const selectedGroupLabel = selectedGroup
    ? t(intl, 'LandingPage.summary.groupValue', '{group}, {people}', {
        group: getOptionLabel(intl, selectedGroup),
        people: t(
          intl,
          planner.people === 1 ? 'LandingPage.people.one' : 'LandingPage.people.other',
          planner.people === 1 ? '{count} person' : '{count} people',
          { count: planner.people }
        ),
      })
    : t(intl, 'LandingPage.summary.chooseGroup', 'Choose group, {people}', {
        people: t(
          intl,
          planner.people === 1 ? 'LandingPage.people.one' : 'LandingPage.people.other',
          planner.people === 1 ? '{count} person' : '{count} people',
          { count: planner.people }
        ),
      });
  const preferenceSummaryItems = useMemo(
    () => [
      { step: 0, label: t(intl, 'LandingPage.summary.activity', 'Activity'), value: selectedDisciplineLabel },
      { step: 1, label: t(intl, 'LandingPage.summary.level', 'Level'), value: getOptionLabel(intl, selectedAbility) },
      { step: 2, label: t(intl, 'LandingPage.summary.place', 'Place'), value: selectedLocationLabel },
      { step: 3, label: t(intl, 'LandingPage.summary.date', 'Date'), value: selectedDateLabel },
      { step: 4, label: t(intl, 'LandingPage.summary.budget', 'Budget'), value: selectedBudgetLabel },
      { step: 5, label: t(intl, 'LandingPage.summary.group', 'Group'), value: selectedGroupLabel },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, selectedDisciplineLabel, selectedAbility, selectedLocationLabel, selectedDateLabel, selectedBudgetLabel, selectedGroupLabel]
  );

  useEffect(() => {
    if (typeof onPlannerChange === 'function') {
      onPlannerChange(preferenceSummaryItems);
    }
  }, [preferenceSummaryItems, onPlannerChange]);

  const canSubmitPlanner = !!planner.group;

  const goToNextPlannerStep = useCallback(() => {
    setPlannerStep(prev => Math.min(prev + 1, PLANNER_STEP_COUNT - 1));
  }, []);

  const goToPreviousPlannerStep = useCallback(() => {
    setPlannerStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleBudgetRangeChange = useCallback((field, value) => {
    const numericValue = value.replace(/[^\d]/g, '');

    setPlanner(prev => ({
      ...prev,
      budgetMode: 'custom',
      [field]: numericValue,
    }));
    setPlannerStep(4);
  }, []);

  const preventBudgetInputSubmit = useCallback(event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const useCurrentPlannerLocation = useCallback(async () => {
    setLocationStatus({
      status: 'loading',
      message: t(intl, 'LandingPage.location.findingCurrentArea', 'Finding your current area...'),
    });

    try {
      const origin = await userLocation({
        enableHighAccuracy: false,
        maximumAge: 3600000,
      });
      const currentLocationBoundsDistance =
        config.maps?.search?.currentLocationBoundsDistance || 30000;
      const bounds = locationBounds(origin, currentLocationBoundsDistance);

      setPlanner(prev => ({
        ...prev,
        locationMode: 'current',
        location: {
          search: t(intl, 'LandingPage.location.currentRawText', 'My current location'),
          predictions: [],
          selectedPlace: {
            address: t(intl, 'LandingPage.location.currentRawText', 'My current location'),
            origin,
            bounds,
          },
        },
        locationCountry: prev.locationCountry,
        locationRegion: null,
      }));
      setLocationStatus({
        status: 'success',
        message: t(intl, 'LandingPage.location.currentSelected', 'Current area selected.'),
      });
    } catch (e) {
      setLocationStatus({
        status: 'error',
        message: t(
          intl,
          'LandingPage.location.currentError',
          'We could not access your location. You can type a city or keep it flexible.'
        ),
      });
    }
  }, [config.maps?.search?.currentLocationBoundsDistance, intl]);

  const runPlanner = useCallback(
    event => {
      event.preventDefault();

      // Block implicit form submissions from native date-picker popups.
      // When the browser fires an implicit submit (e.g. completing the last date field),
      // it happens synchronously in the same JS tick as the input's onChange — so
      // dateJustChangedRef is still true here. A manual button click always arrives
      // in a later tick (dateJustChangedRef is already cleared by setTimeout(0)).
      if (!event.nativeEvent?.submitter || dateJustChangedRef.current) {
        return;
      }

      if (!hasPromptRequest && plannerStep < PLANNER_STEP_COUNT - 1) {
        setPlannerStep(PLANNER_STEP_COUNT - 1);
        return;
      }

      const group = GROUP_OPTIONS.find(option => option.id === planner.group);
      if (!group) {
        setPlannerStep(PLANNER_STEP_COUNT - 1);
        return;
      }

      const shortcut = LOCATION_SHORTCUTS.find(option => option.id === planner.locationMode);
      const locationLabel = getPlannerLocationLabel(planner.location);
      const origin = planner.location?.selectedPlace?.origin;
      const locationPrompt =
        planner.locationMode === 'specific' && locationLabel
          ? `around ${locationLabel}`
          : planner.locationMode === 'current'
          ? origin?.lat && origin?.lng
            ? `near my current location, origin ${origin.lat},${origin.lng}`
            : 'near my current location'
          : planner.locationMode === 'flexible' && locationLabel
          ? `flexible within ${locationLabel}`
          : shortcut
          ? getOptionPrompt(intl, shortcut)
          : t(
              intl,
              'LandingPage.location.flexible.prompt',
              'where Crion has the strongest available matches'
            );
      const disciplineLabel = getLocalizedLabel(intl, planner.discipline);
      const ability = LEVEL_OPTIONS.find(option => option.id === planner.ability) || LEVEL_OPTIONS[1];
      const datePrompt = getDatePreferencePrompt(intl, planner);
      const budgetPrompt = getBudgetPreferencePrompt(intl, planner, currency);
      const budgetSearchPrice = getBudgetSearchPrice(planner);
      const peopleLabel = t(
        intl,
        planner.people === 1 ? 'LandingPage.people.one' : 'LandingPage.people.other',
        planner.people === 1 ? '{count} person' : '{count} people',
        { count: planner.people }
      );
      const parts = [
        `discipline: ${disciplineLabel}`,
        `listing filter pub_discipline=${planner.discipline}`,
        `ability: ${getOptionPrompt(intl, ability)}`,
        `listing filter pub_ability=${planner.ability}`,
        locationPrompt,
        `timing: ${datePrompt}`,
        planner.dateMode === 'specific' && planner.startDate && planner.endDate
          ? `listing availability dates=${planner.startDate},${planner.endDate}`
          : null,
        `budget: ${budgetPrompt}`,
        budgetSearchPrice ? `listing filter price=${budgetSearchPrice}` : null,
        `group: ${getOptionPrompt(intl, group)} for ${peopleLabel}`,
        group.groupType ? `listing filter pub_groupType=${group.groupType}` : null,
        `listing filter pub_people=${planner.people}`,
      ].filter(Boolean);
      const intro = hasPromptRequest
        ? t(
            intl,
            'LandingPage.aiPrompt.updateIntro',
            'Update my Crion AI plan with these revised preferences'
          )
        : t(
            intl,
            'LandingPage.aiPrompt.newIntro',
            'I am a first-time Crion visitor using the Crion AI planner'
          );
      const instruction = hasPromptRequest
        ? t(
            intl,
            'LandingPage.aiPrompt.updateInstruction',
            'Compare against the earlier shortlist, refresh the recommendations, and explain what changed.'
          )
        : t(
            intl,
            'LandingPage.aiPrompt.newInstruction',
            'Start with one short recommendation, then give me 3 strong bookable Crion matches when available.'
          );
      const prompt = t(
        intl,
        'LandingPage.aiPrompt.template',
        '{intro}. Use this brief and do not ask the same setup questions again: {brief}. {instruction} Prioritize listings that match the provided listing filters, explain why each fits, and suggest one clear next action. If the brief is not enough, ask only one follow-up question.',
        {
          intro,
          brief: parts.join(', '),
          instruction,
        }
      );
      const plannerPreferences = getPlannerPreferences({
        intl,
        planner,
        selectedCountry,
        selectedCountryRegions,
        selectedDisciplineLabel: disciplineLabel,
        selectedAbility: ability,
        selectedGroup: group,
        currency,
        source,
      });

      onPromptRequest({
        id: `${Date.now()}-${prompt}`,
        prompt,
        displayMessage: hasPromptRequest
          ? t(intl, 'LandingPage.aiPrompt.updatedDisplayMessage', 'Updated preferences sent to Crion AI')
          : t(intl, 'LandingPage.aiPrompt.displayMessage', 'Preferences sent to Crion AI'),
        context: {
          location: trimText(
            plannerPreferences.location?.label || plannerPreferences.location?.rawText || '',
            128
          ),
          activities: plannerPreferences.discipline ? [plannerPreferences.discipline] : [],
          plannerPreferences,
        },
      });
    },
    [
      currency,
      hasPromptRequest,
      intl,
      onPromptRequest,
      planner,
      plannerStep,
      selectedCountry,
      selectedCountryRegions,
      source,
    ]
  );

  return (
    <div className={css.plannerColumn}>
      {hasPromptRequest ? (
        <div className={css.preferenceSummary}>
          <span className={css.preferenceSummaryTitle}>
            {t(intl, 'LandingPage.preferences.title', 'Preferences')}
          </span>
          <div className={css.preferenceSummaryGrid}>
            {preferenceSummaryItems.map(item => (
              <button
                key={item.label}
                type="button"
                className={classNames(css.preferenceSummaryItem, {
                  [css.preferenceSummaryItemActive]: activePlannerStep === item.step,
                })}
                onClick={() => setPlannerStep(item.step)}
              >
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <form className={css.plannerForm} onSubmit={runPlanner}>
        <div className={css.plannerProgress}>
          <span>
            {t(intl, 'LandingPage.planner.stepProgress', 'Step {step} of {total}', {
              step: activePlannerStep + 1,
              total: PLANNER_STEP_COUNT,
            })}
          </span>
          <div className={css.progressTrack} aria-hidden="true">
            <span
              className={css.progressFill}
              style={{ width: `${((activePlannerStep + 1) / PLANNER_STEP_COUNT) * 100}%` }}
            />
          </div>
        </div>

        <div className={css.plannerStep}>
          {activePlannerStep === 0 ? (
            <>
              <span className={css.plannerStepKicker}>
                {t(intl, 'LandingPage.step.activity.kicker', 'Activity')}
              </span>
              <h3>{t(intl, 'LandingPage.step.activity.title', 'What would you like to try?')}</h3>
              <p>
                {t(
                  intl,
                  'LandingPage.step.activity.description',
                  'Choose a discipline first. This maps directly to Crion listings.'
                )}
              </p>
              <div className={css.disciplineChoiceGrid}>
                {plannerDisciplines.map(discipline => {
                  const active = planner.discipline === discipline.key;
                  return (
                    <button
                      key={discipline.key}
                      type="button"
                      className={classNames(css.disciplineChoice, {
                        [css.disciplineChoiceActive]: active,
                      })}
                      onClick={() => setPlanner(prev => ({ ...prev, discipline: discipline.key }))}
                      aria-pressed={active}
                    >
                      <img
                        src={discipline.srcImg}
                        alt=""
                        className={css.disciplineChoiceImage}
                        loading="lazy"
                      />
                      <span>{getLocalizedLabel(intl, discipline.key)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {activePlannerStep === 1 ? (
            <>
              <span className={css.plannerStepKicker}>
                {t(intl, 'LandingPage.step.level.kicker', 'Skill level')}
              </span>
              <h3>{t(intl, 'LandingPage.step.level.title', 'What level should we match?')}</h3>
              <p>
                {t(
                  intl,
                  'LandingPage.step.level.description',
                  'This keeps recommendations realistic and helps Crion AI avoid experiences that are too easy or too technical.'
                )}
              </p>
              <div className={css.levelChoiceGrid}>
                {LEVEL_OPTIONS.map(level => (
                  <button
                    key={level.id}
                    type="button"
                    className={classNames(css.levelChoice, {
                      [css.levelChoiceActive]: planner.ability === level.id,
                    })}
                    onClick={() => setPlanner(prev => ({ ...prev, ability: level.id }))}
                    aria-pressed={planner.ability === level.id}
                  >
                    <div className={css.levelSignal} aria-hidden="true">
                      {[0, 1, 2].map(index => (
                        <span
                          key={index}
                          className={classNames({
                            [css.levelSignalFilled]: index < level.fill,
                          })}
                        />
                      ))}
                    </div>
                    <strong>{getOptionLabel(intl, level)}</strong>
                    <span>{getOptionText(intl, level)}</span>
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {activePlannerStep === 2 ? (
            <>
              <span className={css.plannerStepKicker}>
                {t(intl, 'LandingPage.step.location.kicker', 'Location')}
              </span>
              <h3>{t(intl, 'LandingPage.step.location.title', 'Where should we start looking?')}</h3>
              <p>
                {t(
                  intl,
                  'LandingPage.step.location.description',
                  'Stay broad with a country or region, use your current area, or type a specific place.'
                )}
              </p>
              <div className={css.locationModeTabs}>
                <button
                  type="button"
                  className={classNames(css.locationModeButton, {
                    [css.locationModeButtonActive]: planner.locationMode === 'flexible',
                  })}
                  onClick={() => {
                    const country = selectedCountry || plannerCountries[0];
                    setPlanner(prev => ({
                      ...prev,
                      locationMode: 'flexible',
                      location: {
                        search: country?.name || '',
                        predictions: [],
                        selectedPlace: { address: country?.name || '' },
                      },
                      locationCountry: country?.id || prev.locationCountry,
                      locationRegion: null,
                    }));
                  }}
                >
                  {t(intl, 'LandingPage.locationMode.flexible', 'Flexible area')}
                </button>
                <button
                  type="button"
                  className={classNames(css.locationModeButton, {
                    [css.locationModeButtonActive]: planner.locationMode === 'specific',
                  })}
                  onClick={() =>
                    setPlanner(prev => ({ ...prev, locationMode: 'specific', locationRegion: null }))
                  }
                >
                  {t(intl, 'LandingPage.locationMode.specific', 'Specific place')}
                </button>
                <button
                  type="button"
                  className={classNames(css.locationModeButton, {
                    [css.locationModeButtonActive]: planner.locationMode === 'current',
                  })}
                  onClick={useCurrentPlannerLocation}
                  disabled={locationStatus.status === 'loading'}
                >
                  {locationStatus.status === 'loading'
                    ? t(intl, 'LandingPage.location.finding', 'Finding...')
                    : t(intl, 'LandingPage.location.nearMe.label', 'Near me')}
                </button>
              </div>

              {planner.locationMode === 'specific' ? (
                <div className={css.aiLocationInputRoot}>
                  <LocationAutocompleteInput
                    rootClassName={css.aiLocationInputRoot}
                    iconClassName={css.aiLocationIcon}
                    inputClassName={css.aiLocationInput}
                    predictionsClassName={css.aiLocationPredictions}
                    placeholder={t(
                      intl,
                      'LandingPage.location.specificPlaceholder',
                      'City, region or mountain area'
                    )}
                    input={{
                      id: 'CrionAIPagePlannerLocation',
                      name: 'CrionAIPagePlannerLocation',
                      value: planner.location,
                      onChange: value =>
                        setPlanner(prev => ({
                          ...prev,
                          location: value,
                          locationMode: 'specific',
                          locationRegion: null,
                        })),
                    }}
                    meta={{ touched: false, error: null }}
                    useDefaultPredictions={false}
                    closeOnBlur
                  />
                </div>
              ) : null}

              {planner.locationMode === 'flexible' ? (
                <>
                  <div className={css.countryChoiceGrid}>
                    {plannerCountries.map(country => {
                      const active = planner.locationCountry === country.id && !planner.locationRegion;
                      return (
                        <button
                          key={country.id}
                          type="button"
                          className={classNames(css.countryChoice, {
                            [css.countryChoiceActive]: active,
                          })}
                          onClick={() =>
                            setPlanner(prev => ({
                              ...prev,
                              locationMode: 'flexible',
                              locationCountry: country.id,
                              locationRegion: null,
                              location: {
                                search: country.name,
                                predictions: [],
                                selectedPlace: { address: country.name },
                              },
                            }))
                          }
                          aria-pressed={active}
                        >
                          <img src={country.image} alt="" className={css.countryChoiceImage} loading="lazy" />
                          <span>{country.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  {selectedCountryRegions.length ? (
                    <div className={css.regionChoiceBlock}>
                      <span>{selectedCountry.name}</span>
                      <div className={css.regionChoiceGrid}>
                        {selectedCountryRegions.map(region => {
                          const active = planner.locationRegion === region.id;
                          return (
                            <button
                              key={region.id}
                              type="button"
                              className={classNames(css.regionChoice, {
                                [css.regionChoiceActive]: active,
                              })}
                              onClick={() =>
                                setPlanner(prev => ({
                                  ...prev,
                                  locationMode: 'flexible',
                                  locationCountry: selectedCountry.id,
                                  locationRegion: region.id,
                                  location: {
                                    search: `${region.name}, ${selectedCountry.name}`,
                                    predictions: [],
                                    selectedPlace: { address: `${region.name}, ${selectedCountry.name}` },
                                  },
                                }))
                              }
                              aria-pressed={active}
                            >
                              {region.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              {planner.locationMode === 'current' ? (
                <div className={css.currentLocationCard}>
                  <strong>
                    {t(intl, 'LandingPage.location.currentTitle', 'Using your current area')}
                  </strong>
                  <span>
                    {t(
                      intl,
                      'LandingPage.location.currentText',
                      'Crion AI can use your approximate area to suggest nearby experiences. You can change it anytime.'
                    )}
                  </span>
                </div>
              ) : null}

              {locationStatus.message ? (
                <span
                  className={classNames(css.locationStatus, {
                    [css.locationStatusError]: locationStatus.status === 'error',
                  })}
                >
                  {locationStatus.message}
                </span>
              ) : null}
            </>
          ) : null}

          {activePlannerStep === 3 ? (
            <>
              <span className={css.plannerStepKicker}>
                {t(intl, 'LandingPage.step.timing.kicker', 'Timing')}
              </span>
              <h3>{t(intl, 'LandingPage.step.timing.title', 'When could you go?')}</h3>
              <p>
                {t(
                  intl,
                  'LandingPage.step.timing.description',
                  'Broad timing works well for discovery. Specific dates help later when availability matters.'
                )}
              </p>
              <div className={css.dateChoiceGrid}>
                {DATE_OPTIONS.map(option => {
                  const active = planner.dateMode === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={classNames(css.dateChoice, {
                        [css.dateChoiceActive]: active,
                      })}
                      onClick={() => setPlanner(prev => ({ ...prev, dateMode: option.id }))}
                      aria-pressed={active}
                    >
                      <strong>{getOptionLabel(intl, option)}</strong>
                      <span>{getOptionText(intl, option)}</span>
                    </button>
                  );
                })}
              </div>
              {planner.dateMode === 'specific' ? (
                <div className={css.dateInputGrid}>
                  <label>
                    <span>{t(intl, 'LandingPage.date.startDate', 'Start date')}</span>
                    <input
                      type="date"
                      value={planner.startDate}
                      onFocus={() => setPlannerStep(3)}
                      onKeyDown={preventBudgetInputSubmit}
                      onChange={event => onDateInputChange('startDate', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>{t(intl, 'LandingPage.date.endDate', 'End date')}</span>
                    <input
                      type="date"
                      value={planner.endDate}
                      onFocus={() => setPlannerStep(3)}
                      onKeyDown={preventBudgetInputSubmit}
                      onChange={event => onDateInputChange('endDate', event.target.value)}
                    />
                  </label>
                </div>
              ) : null}
            </>
          ) : null}

          {activePlannerStep === 4 ? (
            <>
              <span className={css.plannerStepKicker}>
                {t(intl, 'LandingPage.step.budget.kicker', 'Budget')}
              </span>
              <h3>{t(intl, 'LandingPage.step.budget.title', 'What budget feels right?')}</h3>
              <p>
                {t(
                  intl,
                  'LandingPage.step.budget.description',
                  'Keep it flexible if you are exploring. Choose a range if price should guide the first shortlist.'
                )}
              </p>
              <div
                className={css.budgetUnitTabs}
                aria-label={t(intl, 'LandingPage.budget.unitAriaLabel', 'Budget unit')}
              >
                {BUDGET_UNITS.map(unit => {
                  const active = planner.budgetUnit === unit.id;
                  return (
                    <button
                      key={unit.id}
                      type="button"
                      className={classNames(css.budgetUnitButton, {
                        [css.budgetUnitButtonActive]: active,
                      })}
                      onClick={() => setPlanner(prev => ({ ...prev, budgetUnit: unit.id }))}
                      aria-pressed={active}
                    >
                      {getOptionLabel(intl, unit)}
                    </button>
                  );
                })}
              </div>
              <div className={css.budgetChoiceGrid}>
                {BUDGET_OPTIONS.map(option => {
                  const active = planner.budgetMode === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={classNames(css.budgetChoice, {
                        [css.budgetChoiceActive]: active,
                      })}
                      onClick={() => setPlanner(prev => ({ ...prev, budgetMode: option.id }))}
                      aria-pressed={active}
                    >
                      <strong>{formatBudgetOptionLabel(intl, option, currency)}</strong>
                      <span>{getOptionText(intl, option)}</span>
                    </button>
                  );
                })}
              </div>
              {planner.budgetMode === 'custom' ? (
                <div className={css.budgetInputGrid}>
                  <label>
                    <span>
                      {t(intl, 'LandingPage.budget.minLabel', 'Min {currency}', { currency })}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="0"
                      value={planner.budgetMin}
                      onFocus={() => setPlannerStep(4)}
                      onKeyDown={preventBudgetInputSubmit}
                      onChange={event => handleBudgetRangeChange('budgetMin', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>
                      {t(intl, 'LandingPage.budget.maxLabel', 'Max {currency}', { currency })}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="500"
                      value={planner.budgetMax}
                      onFocus={() => setPlannerStep(4)}
                      onKeyDown={preventBudgetInputSubmit}
                      onChange={event => handleBudgetRangeChange('budgetMax', event.target.value)}
                    />
                  </label>
                </div>
              ) : null}
            </>
          ) : null}

          {activePlannerStep === 5 ? (
            <>
              <span className={css.plannerStepKicker}>
                {t(intl, 'LandingPage.step.group.kicker', 'Group')}
              </span>
              <h3>{t(intl, 'LandingPage.step.group.title', 'Who is going?')}</h3>
              <p>
                {t(
                  intl,
                  'LandingPage.step.group.description',
                  'Choose the booking style and number of people. The AI will use this to avoid impossible matches.'
                )}
              </p>
              <div className={css.groupChoiceGrid}>
                {GROUP_OPTIONS.map(group => (
                  <button
                    key={group.id}
                    type="button"
                    className={classNames(css.groupChoice, {
                      [css.groupChoiceActive]: planner.group === group.id,
                    })}
                    onClick={() => setPlanner(prev => ({ ...prev, group: group.id }))}
                    aria-pressed={planner.group === group.id}
                  >
                    <strong>{getOptionLabel(intl, group)}</strong>
                    <span>{getOptionText(intl, group)}</span>
                  </button>
                ))}
              </div>
              <div className={css.peopleSelector}>
                <div>
                  <strong>{t(intl, 'LandingPage.people.title', 'Number of people')}</strong>
                  <span>
                    {t(
                      intl,
                      'LandingPage.people.description',
                      'This helps match private and open group availability.'
                    )}
                  </span>
                </div>
                <div
                  className={css.peopleStepper}
                  aria-label={t(intl, 'LandingPage.people.title', 'Number of people')}
                >
                  <button
                    type="button"
                    className={css.peopleButton}
                    onClick={() =>
                      setPlanner(prev => ({
                        ...prev,
                        people: Math.max(PEOPLE_MIN, prev.people - 1),
                      }))
                    }
                    disabled={planner.people <= PEOPLE_MIN}
                    aria-label={t(
                      intl,
                      'LandingPage.people.decrease',
                      'Decrease number of people'
                    )}
                  >
                    -
                  </button>
                  <span className={css.peopleCount}>{planner.people}</span>
                  <button
                    type="button"
                    className={css.peopleButton}
                    onClick={() =>
                      setPlanner(prev => ({
                        ...prev,
                        people: Math.min(PEOPLE_MAX, prev.people + 1),
                      }))
                    }
                    disabled={planner.people >= PEOPLE_MAX}
                    aria-label={t(
                      intl,
                      'LandingPage.people.increase',
                      'Increase number of people'
                    )}
                  >
                    +
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className={css.plannerNav}>
          {activePlannerStep > 0 ? (
            <button type="button" className={css.plannerBackButton} onClick={goToPreviousPlannerStep}>
              {t(intl, 'LandingPage.actions.back', 'Back')}
            </button>
          ) : (
            <span />
          )}
          {hasPromptRequest ? (
            <div className={css.plannerNavActions}>
              {activePlannerStep < PLANNER_STEP_COUNT - 1 ? (
                <button type="button" className={css.plannerBackButton} onClick={goToNextPlannerStep}>
                  {t(intl, 'LandingPage.actions.nextPreference', 'Next preference')}
                </button>
              ) : null}
              <button type="submit" className={css.plannerSubmit} disabled={!canSubmitPlanner}>
                <IconQuestionAnswer className={css.buttonIcon} />
                <span>{t(intl, 'LandingPage.actions.updateAiPlan', 'Update AI plan')}</span>
              </button>
            </div>
          ) : activePlannerStep < PLANNER_STEP_COUNT - 1 ? (
            <button type="button" className={css.plannerSubmit} onClick={goToNextPlannerStep}>
              {t(intl, 'LandingPage.actions.continue', 'Continue')}
            </button>
          ) : (
            <button type="submit" className={css.plannerSubmit} disabled={!canSubmitPlanner}>
              <IconQuestionAnswer className={css.buttonIcon} />
              <span>{t(intl, 'LandingPage.actions.buildAiPlan', 'Build my AI plan')}</span>
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

AIPlannerPreferences.defaultProps = {
  hasPromptRequest: false,
  onPlannerChange: null,
  persistKey: null,
  source: 'crion-ai',
};

AIPlannerPreferences.propTypes = {
  hasPromptRequest: bool,
  intl: intlShape.isRequired,
  locale: string.isRequired,
  onPlannerChange: func,
  onPromptRequest: func.isRequired,
  persistKey: string,
  source: string,
};

export default AIPlannerPreferences;
